import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/toast";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { user, resetPassword } = useAuth();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        type: "error",
        description: "Please enter your email address",
      });
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(email);
      setIsSubmitted(true);
      toast({
        type: "success",
        description: "Password reset email sent. Please check your inbox.",
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast({
        type: "error",
        description:
          error.message || "Failed to send reset email. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex h-dvh w-screen items-start pt-12 md:pt-0 md:items-center justify-center bg-background">
        <div className="w-full max-w-md overflow-hidden rounded-2xl flex flex-col gap-6">
          <div className="flex flex-col items-center justify-center gap-2 px-4 text-center sm:px-16">
            <h3 className="text-xl font-semibold dark:text-zinc-50">
              Check Your Email
            </h3>
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              We've sent password reset instructions to {email}
            </p>
            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-4">
              Didn't receive the email? Check your spam folder or{" "}
              <button
                onClick={() => setIsSubmitted(false)}
                className="font-semibold text-gray-800 hover:underline dark:text-zinc-200"
              >
                try again
              </button>
            </p>
            <Link
              to="/login"
              className="mt-4 font-semibold text-gray-800 hover:underline dark:text-zinc-200"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-screen items-start pt-12 md:pt-0 md:items-center justify-center bg-background">
      <div className="w-full max-w-md overflow-hidden rounded-2xl flex flex-col gap-12">
        <div className="flex flex-col items-center justify-center gap-2 px-4 text-center sm:px-16">
          <h3 className="text-xl font-semibold dark:text-zinc-50">
            Forgot Password?
          </h3>
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            Enter your email address and we'll send you instructions to reset
            your password
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-4 sm:px-16">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-xs text-gray-600 uppercase dark:text-zinc-400"
              >
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="user@example.com"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder:text-gray-400 shadow-sm focus:border-black focus:outline-none focus:ring-black dark:border-gray-600 dark:bg-black dark:text-white dark:placeholder:text-gray-500 dark:focus:border-white sm:text-sm"
              />
            </div>

            <div className="w-full flex justify-center">
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full size-4 border-b-2 border-primary-foreground mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  "Send Reset Instructions"
                )}
              </button>
            </div>

            <p className="text-center text-sm text-gray-600 dark:text-zinc-400">
              Remember your password?{" "}
              <Link
                to="/login"
                className="font-semibold text-gray-800 hover:underline dark:text-zinc-200"
              >
                Back to login
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
